import type { ConsolePlan } from "@wallyo/contracts/console/billing";
import { useRef, useState } from "react";
import { useApi } from "../../api/context";
import { createIntent } from "../../api/idempotency";
import type { WriteFailure } from "../team/team-format";
import { toLocalInput } from "../local-datetime";
import {
  billingOutcome,
  billingProblem,
  describeBillingFailure,
  sendBillingWrite,
  EMPTY_BILLING_FIELDS,
  type BillingFields,
  type BillingOutcome,
  type BillingTarget,
} from "./billing-actions";
import { BillingDialog } from "./BillingDialog";

export type BillingWriteDialogProps = {
  /** Null: no dialog is open, so callers can always render this. */
  target: BillingTarget | null;
  /** What the plan selects offer; empty for the writes that need none. */
  plans: readonly ConsolePlan[];
  /** A remise opened from a pro's page already names them. */
  workerId: string | null;
  /** Picking a pro in the remise form reads their account (VIEW_ACCOUNTS). */
  canViewAccounts: boolean;
  onClose: () => void;
  onReload: () => void;
  onDone: (outcome: BillingOutcome) => void;
};

/** What a form starts with. */
function firstFields(target: BillingTarget, workerId: string | null, now: Date): BillingFields {
  if (target.kind === "newDiscount") {
    return { ...EMPTY_BILLING_FIELDS, scope: "pro", workerId: workerId ?? "", startsAt: toLocalInput(now) };
  }
  if (target.kind === "newPromo") return { ...EMPTY_BILLING_FIELDS, startsAt: toLocalInput(now) };
  return EMPTY_BILLING_FIELDS;
}

/**
 * One billing write, one intent: the same Idempotency-Key across "try again"
 * after a failure or a lost answer, and a fresh one once anything is edited
 * or the write succeeded (ADR-0016 §2.1). Nothing is shown as done before
 * the server says so.
 */
export function BillingWriteDialog(props: BillingWriteDialogProps) {
  if (!props.target) return null;
  return <WriteForm key={props.target.kind} {...props} target={props.target} />;
}

function WriteForm({
  target,
  plans,
  workerId,
  canViewAccounts,
  onClose,
  onReload,
  onDone,
}: BillingWriteDialogProps & { target: BillingTarget }) {
  const { client } = useApi();
  const [fields, setFields] = useState<BillingFields>(() => firstFields(target, workerId, new Date()));
  const [problem, setProblem] = useState<string | null>(null);
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [pending, setPending] = useState(false);
  const intent = useRef(createIntent());
  const inFlight = useRef(false);

  const submit = async () => {
    if (inFlight.current) return;
    const found = billingProblem(target.kind, fields);
    if (found) {
      setProblem(found);
      return;
    }
    inFlight.current = true;
    setProblem(null);
    setFailure(null);
    setPending(true);
    try {
      const result = await sendBillingWrite(client, target, fields, intent.current.key());
      intent.current.reset();
      onDone(billingOutcome(result));
    } catch (error) {
      setPending(false);
      setFailure(describeBillingFailure(error));
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <BillingDialog
      target={target}
      plans={plans}
      fields={fields}
      problem={problem}
      failure={failure}
      pending={pending}
      canViewAccounts={canViewAccounts}
      onFields={(next) => {
        intent.current.reset();
        setFields((was) => ({ ...was, ...next }));
        setProblem(null);
      }}
      onSubmit={() => void submit()}
      onClose={onClose}
      onReload={onReload}
    />
  );
}
